/**
 * The connected card terminal (`cardTerminal.mode === 'connected'`).
 *
 * The manual mode next to it is staff work: the guest taps the card button,
 * staff run the card on their own EDC and confirm on the booth. A connected
 * terminal does that leg by itself — Studio pushes the amount to a Mercado
 * Pago Point terminal or a Nayax reader standing beside the booth and reports
 * the order's status on every poll (`point_status`), so nobody has to key an
 * approval code. Studio speaks the same status words for both.
 *
 * What the payment page does with that status lives here, free of React, so
 * the rules can be tested:
 *
 * - `created`: the terminal has not picked the order up yet. Studio can still
 *   cancel it.
 * - `at_terminal` / `action_required`: the customer is paying on the terminal.
 *   Only the terminal can cancel it now, so the booth must not reset the
 *   session under them.
 * - `failed` / `canceled`: declined, or cancelled on the terminal. The
 *   customer can try again with a new order.
 * - `processed`: paid.
 * - `expired` / `refunded`: over.
 *
 * An unknown or missing status (Mercado Pago unreachable, or a status this
 * build does not know) counts as "may still be in progress": never as paid,
 * and never as safe to walk away from.
 */

export type PointOrderStatus =
  | 'created'
  | 'at_terminal'
  | 'processed'
  | 'action_required'
  | 'failed'
  | 'canceled'
  | 'expired'
  | 'refunded'

const POINT_ORDER_STATUSES: readonly PointOrderStatus[] = [
  'created',
  'at_terminal',
  'processed',
  'action_required',
  'failed',
  'canceled',
  'expired',
  'refunded'
]

export function normalizePointStatus(value: unknown): PointOrderStatus | null {
  const status = String(value ?? '')
    .trim()
    .toLowerCase()
  return (POINT_ORDER_STATUSES as readonly string[]).includes(status)
    ? (status as PointOrderStatus)
    : null
}

/** The customer is paying on the terminal right now. */
export function isPointAtTerminal(status: PointOrderStatus | null): boolean {
  return status === 'at_terminal' || status === 'action_required'
}

/** Declined or cancelled on the terminal: a fresh order can be tried. */
export function isPointRetryable(status: PointOrderStatus | null): boolean {
  return status === 'failed' || status === 'canceled'
}

/**
 * What the page does when its own payment countdown reaches zero.
 *
 * - `none`: already paid; the success path handles it.
 * - `wait`: the customer is mid-payment; keep polling until the order settles.
 * - `cancel`: ask Studio to cancel the order (only possible before the terminal
 *   has it), and expire once that succeeds.
 * - `expire`: nothing is in flight any more.
 */
export type PointCountdownAction = 'none' | 'wait' | 'cancel' | 'expire'

export function pointCountdownAction(status: PointOrderStatus | null): PointCountdownAction {
  switch (status) {
    case 'processed':
      return 'none'
    case 'at_terminal':
    case 'action_required':
      return 'wait'
    case 'failed':
    case 'canceled':
    case 'expired':
    case 'refunded':
      return 'expire'
    case 'created':
    default:
      return 'cancel'
  }
}

/** Outcome of `DELETE /api/payments/[orderId]` for a card-terminal order. */
export type PointCancelOutcome = 'cancelled' | 'skipped' | 'at_terminal' | 'processed' | 'unknown'

export function parsePointCancelResponse(httpStatus: number, body: unknown): PointCancelOutcome {
  const status =
    body && typeof body === 'object' ? String((body as { status?: unknown }).status ?? '') : ''
  if (httpStatus === 409 || status === 'at_terminal') return 'at_terminal'
  if (status === 'processed') return 'processed'
  if (httpStatus >= 200 && httpStatus < 300) {
    if (status === 'cancelled') return 'cancelled'
    if (status === 'skipped') return 'skipped'
  }
  return 'unknown'
}

/**
 * May the booth leave the payment screen after this cancel outcome?
 *
 * `skipped` counts as leaving-safe. Studio answers it when the order is
 * already dead (cancelled, expired, failed, refunded), when the reference does
 * not belong to this order, and when it cannot resolve the order at all. An
 * order it can neither read nor cancel is one no amount of waiting will
 * resolve, and a paid one is never `skipped`: that answers `processed`.
 */
export function canLeaveAfterCancel(outcome: PointCancelOutcome): boolean {
  return outcome === 'cancelled' || outcome === 'skipped'
}

/**
 * The line under the terminal icon on the card panel, as an i18n key with its
 * English fallback. The keys sit in the same `payment.card.*` namespace as the
 * staff-confirmed flow, because to the guest it is one card payment either way.
 *
 * `windowOver` is true once the booth's countdown has ended while the order was
 * still open.
 */
export function pointStatusMessage(
  status: PointOrderStatus | null,
  windowOver: boolean
): { key: string; fallback: string } {
  if (windowOver && (status === null || status === 'created' || isPointAtTerminal(status))) {
    return {
      key: 'payment.card.terminalFinishing',
      fallback: 'Finish the payment on the terminal. This screen continues on its own.'
    }
  }
  switch (status) {
    case 'at_terminal':
      return {
        key: 'payment.card.terminalTapCard',
        fallback: 'Tap, insert or swipe your card on the terminal'
      }
    case 'action_required':
      return {
        key: 'payment.card.terminalCheck',
        fallback: 'Check the terminal to finish the payment'
      }
    case 'failed':
      return {
        key: 'payment.card.terminalDeclined',
        fallback: 'The card was declined. Try again or use another card.'
      }
    case 'canceled':
      return {
        key: 'payment.card.terminalCanceled',
        fallback: 'The payment was cancelled on the terminal.'
      }
    case 'processed':
      return { key: 'payment.successMessage', fallback: 'Payment successful!' }
    case 'expired':
    case 'refunded':
      return { key: 'payment.card.terminalExpired', fallback: 'Payment time is up' }
    case 'created':
    default:
      return {
        key: 'payment.card.terminalSending',
        fallback: 'Sending the amount to the terminal...'
      }
  }
}

/**
 * Which integration drives a connected terminal. Booths paired before Nayax
 * existed have no `provider` stored, and every one of them is a Mercado Pago
 * Point terminal.
 */
export type ConnectedTerminalProvider = 'mercadopago' | 'nayax'

export function connectedTerminalProvider(settings: {
  provider?: unknown
}): ConnectedTerminalProvider {
  return settings.provider === 'nayax' ? 'nayax' : 'mercadopago'
}

/**
 * Whether the operator can pick "Connected terminal" in Payments settings.
 *
 * Two integrations exist. Mercado Pago Point is Mexico only, so it opens up
 * for MXN projects. A Nayax reader works in any country Nayax acquires in,
 * so it opens up wherever the operator has connected a Nayax account in
 * Studio (`hasNayaxAccount`). A booth that already has a terminal saved always
 * sees the option too, so the pairing can be changed or cleared after the
 * booth moves to another project.
 */
export function isConnectedTerminalAvailable(
  resolvedCurrency: unknown,
  terminalId: unknown,
  hasNayaxAccount: boolean = false
): boolean {
  return (
    String(resolvedCurrency ?? '').toUpperCase() === 'MXN' ||
    Boolean(terminalId) ||
    hasNayaxAccount
  )
}

/**
 * Is the connected card button real on this charge? It needs a terminal
 * paired with THIS booth and the integration that drives it: a Nayax reader
 * is its own rail beside the project's QR gateway, while Point rides on the
 * project's Mercado Pago gateway.
 */
export function isConnectedCardReady(
  settings: { mode?: unknown; terminalId?: unknown; provider?: unknown },
  activeProvider: unknown
): boolean {
  if (settings.mode !== 'connected' || !settings.terminalId) return false
  return connectedTerminalProvider(settings) === 'nayax' || activeProvider === 'mercadopago'
}

/**
 * A Nayax reader is addressed by the serial printed on it (16 digits) or by
 * its Nayax machine id. Spaces and dashes an operator types along are dropped.
 */
export function normalizeNayaxTerminalId(value: string): string {
  return String(value ?? '').replace(/[\s-]/g, '')
}

export function isPlausibleNayaxTerminalId(value: string): boolean {
  return /^\d{5,20}$/.test(value)
}

/**
 * The serial printed on the terminal's label: the part of the terminal id after
 * `__` (`NEWLAND_N950__N950NCB801293324` -> `N950NCB801293324`).
 */
export function terminalSerial(terminalId: string): string {
  const index = terminalId.lastIndexOf('__')
  return index >= 0 ? terminalId.slice(index + 2) : terminalId
}
